import PropTypes from "prop-types";
import { connect } from "react-redux";
import React, { Component } from "react";
import Button from "@mui/material/Button";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_DATE } from "typeConstants/dataTypes";
import { getAppraisalFormData, setAppraisalFormData } from "actions/homeowner/caseforms";
import get1004pFormDefinition, { get1004pFormValidationGroups, getEnabledKvpForElectricalServiceRepeat } from "components/Homeowner/CaseForms/Appraisal1004P/V2/get1004pFormDefinition";
import moment from "moment-timezone";
import responseHandler from "responseHandler";
import { cloneDeep } from "lodash";
import ScrollToRequired from "components/FormElements/ScrollToRequired";

class Appraisal1004pV2 extends Component {
  constructor(props) {
    super(props);

    this.getFormData = this.getFormData.bind(this);
    this.saveFormData = this.saveFormData.bind(this);

    this.on1004pChange = this.on1004pChange.bind(this);
    this.getDateFields = this.getDateFields.bind(this);
    this.getRepeatIds = this.getRepeatIds.bind(this);

    this.mcardControlMethods = null;
    this.comparableCount = null;

    this.formControlMethods = null;

    this.get1004pFormDefinition = cloneDeep(get1004pFormDefinition);

    this.makeToast = this.makeToast.bind(this);
    this.getError = this.getError.bind(this);
    this.updateFormDefinitions = this.updateFormDefinitions.bind(this);
    this.getRepeatElementCount = this.getRepeatElementCount.bind(this);
    this.getRepeatElementFormDefinition = this.getRepeatElementFormDefinition.bind(this);
    this.insertRepeatElementsInFormDefinition = this.insertRepeatElementsInFormDefinition.bind(this);
    this.makeRepeatElements = this.makeRepeatElements.bind(this);
    this.getContainerDiv = this.getContainerDiv.bind(this);
    this.setElementIdInFocus = this.setElementIdInFocus.bind(this);
    this.containerDiv = React.createRef();

    this.state = {
      formData: null,
      shouldRenderGridForm: true,
      scrollToRequiredFields: [],
      elementIdInFocus: null,
    };
  }

  componentDidMount() {
    this.props.getAppraisalFormData(this.props.controlNumber);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isFetchingAppraisalFormData && !this.props.isFetchingAppraisalFormData) {
      this.setState({ formData: this.getFormData(this.props.dataForAppraisalForm) });
      this.updateFormDefinitions(this.props.dataForAppraisalForm);
    }
  }

  getDateFields() {
    return get1004pFormDefinition.flat().filter(f => f.dataType === DATA_TYPE_DATE).map(f => f.source);
  }

  // Key should be the repeat loop's export id
  getRepeatIds() {
    return {
      "comparables": ["FA-380", "FN-381", "FN-382", "FA-383"],
      "electrical-service": ["electrical-service-2", "off-grid-service", "solar-panel-ownership"]
    };
  }

  updateFormDefinitions(form) {
    if (!form) return null;
    let repeatElementExportId = "electrical-service";
    let repeatElementSource = "electrical-service-2";
    let repeatElementCount = this.getRepeatElementCount(repeatElementExportId, form); //int
    let repeatElementFormDefinition = this.getRepeatElementFormDefinition(repeatElementSource); //new []
    let repeatElementsToInsert = this.makeRepeatElements(repeatElementCount, getEnabledKvpForElectricalServiceRepeat, repeatElementFormDefinition); //new [[]]
    this.insertRepeatElementsInFormDefinition(repeatElementSource, repeatElementsToInsert);
  }

  getRepeatElementCount(repeatElementExportId, form) {
    let repeatElement = form.repeats.find(r => r.id === repeatElementExportId);
    return repeatElement ? repeatElement.groups.length : 0;
  }

  getRepeatElementFormDefinition(repeatElementSource) {
    return this.get1004pFormDefinition.find(r => r.some(e => e.source === repeatElementSource));
  }

  makeRepeatElements(repeatElementCount, enabledKvpFunc, repeatElementFormDefinition) {
    let repeatElementsToInsert = [];
    if (repeatElementCount === 0 || !repeatElementFormDefinition) return repeatElementsToInsert;
    for (let i = 1; i <= repeatElementCount; i++) {
      let newRepeatElementWithUpdatedSource = repeatElementFormDefinition.map(e => ({
        ...e,
        source: `${e.source}-${i}`,
        enabled: enabledKvpFunc(i)[`${e.source}-${i}`]
      }));
      repeatElementsToInsert.push(newRepeatElementWithUpdatedSource);
    }
    return repeatElementsToInsert;
  }

  insertRepeatElementsInFormDefinition(repeatElementSource, repeatElementsToInsert) {
    this.get1004pFormDefinition.splice(this.get1004pFormDefinition.findIndex(r => r.some(e => e.source === repeatElementSource)), 1, ...repeatElementsToInsert);
  }

  getFormData(form) {
    if (!form) return null;

    let dates = this.getDateFields();

    //take YYYY-MM-DD pnx dates and make them MM-DD-YYYY
    let fixDate = d => {
      let split = d.split("-");
      return [split[1], split[2], split[0]].join("/");
    };

    let flattened = form.controls.reduce((acc, val) => {
      acc[val.id] = val.value;

      if (dates.indexOf(val.id) >= 0 && typeof val.value === "string" && val.value) {
        acc[val.id] = fixDate(val.value);
      }

      return acc;
    }, {});

    let repeats = form.repeats.filter(r => ["comparables", "electrical-service"].includes(r.id));
    let comparableGroups = repeats.find(r => r.id === "comparables").groups;

    this.comparableCount = comparableGroups.length;
    let repeatIds = this.getRepeatIds();
    let allRepeatIds = Object.keys(repeatIds).reduce((acc, cur) => acc = [...acc, ...repeatIds[cur]], []);
    repeats.forEach(r =>
      r.groups.forEach((g, i) =>
        g.controls.forEach(c => {
          if (allRepeatIds.find(id => id === c.id)) {
            let key = `${c.id}-${i + 1}`;
            let val = c.value;

            if (dates.indexOf(key) >= 0 && typeof val === "string") {
              val = fixDate(val);
            }

            flattened[key] = val;
          }
        })));

    flattened["comparable-count"] = this.comparableCount; //store this so we can use it in mgridform validation

    let controlHasData = (group, field) => {
      let control = group.controls.find(c => c.id === field);
      return !!(control && control.value.trim());
    };

    //mark comps enabled/disabled based on wether the comp grid has data for them
    comparableGroups.map(g => controlHasData(g, "FN-288") && controlHasData(g, "FN-289") && controlHasData(g, "FN-290") && controlHasData(g, "FN-291"))
      .forEach((enabled, i) => flattened[`comparable-enabled-${i + 1}`] = enabled);

    return flattened;
  }

  saveFormData() {
    let modifiedData = { controls: this.formControlMethods.getModifiedData() };

    let dates = this.getDateFields();
    let fixDate = d => moment(new Date(d)).format("YYYY-MM-DD");
    //change date format to something that phoenix understands
    modifiedData.controls.forEach(e => { if (dates.indexOf(e.id) >= 0 && typeof e.value === "string") e.value = fixDate(e.value); });

    //find the changed repeats that have ids that we care about
    //ex repeat id "FA-380" is represented 3-6 times in the form because the repeat has 3-6 controls, so it will be "FA-380-1", "FA-380-2", "FA-380-3", etc

    let repeatIds = this.getRepeatIds();
    Object.keys(repeatIds).forEach(k => {
      let changedRepeats = modifiedData.controls.map(c => c.id).filter(f => repeatIds[k].reduce((acc, id) => f.startsWith(id + "-") || acc, false));

      if (changedRepeats?.length) {
        //need to use .map instead of passing { controls: [] } into .fill because fill would just put the same reference in every time instead of constructing a new one
        let repeat = { id: k, groups: Array(this.getRepeatElementCount(k, this.props.dataForAppraisalForm)).fill().map(() => ({ controls: [] })) };

        modifiedData.repeats = modifiedData.repeats ? [...modifiedData.repeats, repeat] : [repeat];

        changedRepeats.forEach(r => {
          //changed repeats in the case of the 1004p form are FN-380 - FN-383 and then they have -# after denoting the repeat index (1 based) example FN-381-2 is index 1 on repeats(where id = "comparables").groups.controls
          let split = r.split("-");
          let index = parseInt(split.pop()) - 1;
          let source = split.join("-");

          let val = modifiedData.controls.find(e => e.id === r).value;

          //This is an exception. It is a field in Ext property form which accepts 1/0 for boolean.
          if (k === "electrical-service" && typeof val === "boolean") val = val ? "1" : "0";

          repeat.groups[index].controls.push({ id: source, value: val });
        });
      }
    });
    this.props.setAppraisalFormData(this.props.controlNumber, modifiedData, () => {
      if (this.props.onSave) this.props.onSave();
      this.setState({ formData: null });
      this.props.getAppraisalFormData(this.props.controlNumber);
    });
    if (!this.formControlMethods.isFormValid())
      this.makeToast("Save successful with failed validation.", "warning");
    else
      this.makeToast("Save successful. No validation errors.", "success");
  }

  // ------------------------------------------------------------------------------------------------
  // Toast and Error message helpers
  makeToast(text, type) { this.props.makeToast(text, type); }

  //simple method that will cause a controls error text to show if the variable passed in is not null or empty
  //pass true for meta if this is going to be used on an InputField because it is a custom control that handles errors different
  getError(message, meta) {
    if (message) {
      return meta
        ? { meta: { error: message, touched: true } }
        : { error: true, errorText: message };
    }
  }

  on1004pChange() {
    if (this.props.onChange) this.props.onChange();
  }

  getContainerDiv() {
    return this.containerDiv ?.current;
  }

  setElementIdInFocus(id) { this.setState({ elementIdInFocus: id }); }
  render() {
    return (
      this.state.shouldRenderGridForm &&
      <div className="appraisal-1004p-mgridform">
        <div className="appraisal-1004p-mgridform-header-row">
          {!this.props.readOnly &&
            <div className="appraisal-1004p-mgridform-save">
              <Button
                onClick={this.saveFormData}
              >
                Save
              </Button>
            </div>
          }
          {
            this.getContainerDiv() &&
            <ScrollToRequired fields={this.state.scrollToRequiredFields} containerDiv={this.getContainerDiv()} onChange={(prevId, curId) => {
              this.setState({ elementIdInFocus: curId });
            }} />
          }
        </div>
        <div className="appraisal-1004p-mgridform-form" ref={this.containerDiv}>
          <MGridForm
            width={1200}
            data={this.state.formData}
            onChange={this.on1004pChange}
            formDefinition={this.state.formData && (this.props.readOnly ? this.get1004pFormDefinition.map(x => x.map(y => ({ ...y, enabled: () => false }))) : this.get1004pFormDefinition)}
            getControlMethods={cm => this.formControlMethods = cm}
            validationGroups={get1004pFormValidationGroups}
            onScrollToRequiredFieldsChange={o => this.setState({ scrollToRequiredFields: o })}
            setElementIdInFocus={this.setElementIdInFocus}
            elementIdInFocus={this.state.elementIdInFocus} />
        </div>
      </div>
    );
  }
}

Appraisal1004pV2.propTypes = {
  controlNumber: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  getAppraisalFormData: PropTypes.func.isRequired,
  setAppraisalFormData: PropTypes.func.isRequired,
  isFetchingAppraisalFormData: PropTypes.bool,
  isSavingAppraisalFormData: PropTypes.bool,
  dataForAppraisalForm: PropTypes.object,
  makeToast: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

const mapStateToProps = (state) => ({
  isFetchingAppraisalFormData: state.caseForms.isFetchingAppraisalFormData,
  isSavingAppraisalFormData: state.caseForms.isSavingAppraisalFormData,
  dataForAppraisalForm: state.caseForms.dataForAppraisalForm,
});

const mapDispatchToProps = (dispatch) => ({
  getAppraisalFormData(controlNumber) {
    dispatch(getAppraisalFormData(controlNumber));
  },
  setAppraisalFormData(controlNumber, modifiedData, callback) {
    dispatch(setAppraisalFormData(controlNumber, modifiedData, callback));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Appraisal1004pV2);
